import React, { useState, useCallback, useMemo } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Tooltip, TooltipTrigger, TooltipContent } from '../../ui/tooltip'
import { useContentHeight } from '../../../hooks/useContentHeight'
import { collapseMotion } from '../../../lib/motion/collapseMotion'
import './SidebarSection.scss'

/** Abstand unter dem Titel einer eingeklappten Gruppe — sonst stieße er an den nächsten. */
const COLLAPSED_GAP = 8

const STORAGE_PREFIX = 'sidebarGroup.'

/**
 * Reads the stored expand state of one group.
 * The title is unusable as a key — it is translated, and the same German string
 * belongs to three different i18n keys ("Heute & Danach", "Entwicklung").
 * Hence the explicit, life-space-unique `id`.
 */
const readStored = (id: string, fallback: boolean): boolean => {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + id)
    // Parse explicitly — anything unexpected (nothing stored, a stubbed storage,
    // a stale value from an older version) must fall back, not collapse the group.
    if (raw === 'true') return true
    if (raw === 'false') return false
    return fallback
  } catch {
    return fallback
  }
}

const writeStored = (id: string, value: boolean): void => {
  try {
    localStorage.setItem(STORAGE_PREFIX + id, String(value))
  } catch {
    /* private mode / storage disabled — the group still toggles for this session */
  }
}

export interface SidebarSectionProps {
  /** Stable, life-space-unique key for the persisted expand state. */
  id: string
  title: string
  defaultExpanded?: boolean
  collapsible?: boolean
  actionIcon?: React.ComponentType<{ size?: number }>
  onAction?: () => void
  /** Tooltip for the action button. Falls back to a generic label built from `title`,
   *  which pluralises wrongly for groups whose action creates a single item. */
  actionLabel?: string
  children: React.ReactNode
}

export const SidebarSection: React.FC<SidebarSectionProps> = ({
  id,
  title,
  defaultExpanded = true,
  collapsible = true,
  actionIcon: ActionIcon,
  onAction,
  actionLabel,
  children
}) => {
  const { t } = useTranslation()
  // Read lazily on mount: StandardSidebar keys its content container on lifeSpace,
  // so every life-space switch remounts this and re-reads the stored state.
  const [isExpanded, setIsExpanded] = useState(() => readStored(id, defaultExpanded))

  const setExpanded = useCallback((next: boolean) => {
    setIsExpanded(next)
    writeStored(id, next)
  }, [id])

  /*
    **Auf und zu fahren** (Prototyp `tools/sidebar-groups-motion-prototype`, Variante C, 02.10.2026):
    erst der Platz, dann die Einträge aus 4 px oben; zu erst die Einträge, dann der Platz. Bis dahin
    blendete nur das Aufklappen ein, und zugeklappt fiel die Gruppe per `display: none` weg — alles
    darunter sprang. Die Höhe wird gemessen (`useContentHeight`), damit sie auch fährt, wenn sich
    der Inhalt im Stand ändert (ein Projekt kommt dazu).
  */
  const reduced = useReducedMotion() ?? false
  const [measureRef, height] = useContentHeight(!reduced)
  const collapse = useMemo(() => collapseMotion({ height, closedHeight: COLLAPSED_GAP, reduced }), [height, reduced])

  const handleToggle = () => {
    if (collapsible) setExpanded(!isExpanded)
  }

  const handleAction = (e: React.MouseEvent) => {
    e.stopPropagation()
    // Creating something must make it visible — unlike merely navigating,
    // which deliberately leaves a collapsed group closed.
    if (collapsible) setExpanded(true)
    onAction?.()
  }

  const contentId = `sidebar-group-${id}`
  const actionText = actionLabel ?? t('sidebar.addItem', { title })

  const titleGroup = (
    <>
      <span className="sidebar-section__title">{title}</span>
      {collapsible && (
        <span className={`sidebar-section__chevron${isExpanded ? ' sidebar-section__chevron--open' : ''}`}>
          <ChevronRight size={12} />
        </span>
      )}
    </>
  )

  return (
    <div className={`sidebar-section ${!isExpanded ? 'sidebar-section--collapsed' : ''}`}>
      <div className={`sidebar-section__header ${collapsible ? 'sidebar-section__header--collapsible' : ''}`}>
        {collapsible ? (
          // A real <button> so keyboard and aria-expanded come for free. The action
          // button below is a sibling, not a child — a button inside a button is invalid.
          <button
            type="button"
            className="sidebar-section__title-group"
            onClick={handleToggle}
            aria-expanded={isExpanded}
            aria-controls={contentId}
          >
            {titleGroup}
          </button>
        ) : (
          <div className="sidebar-section__title-group">{titleGroup}</div>
        )}

        {ActionIcon && onAction && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                className="sidebar-section__action"
                onClick={handleAction}
                aria-label={actionText}
              >
                <ActionIcon size={16} />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right">{actionText}</TooltipContent>
          </Tooltip>
        )}
      </div>

      {/* Bleibt gemountet und fährt zu, statt wegzufallen; zugeklappt nimmt `inert` die Einträge
          aus Tastatur und Zeiger. `initial={false}`: beim Aufbau steht sie, wie gemerkt. */}
      <motion.div
        id={contentId}
        className="sidebar-section__content"
        initial={false}
        animate={isExpanded ? collapse.clip.open : collapse.clip.closed}
        inert={!isExpanded}
      >
        <motion.div
          ref={measureRef}
          className="sidebar-section__body"
          initial={false}
          animate={isExpanded ? collapse.body.open : collapse.body.closed}
        >
          {children}
        </motion.div>
      </motion.div>
    </div>
  )
}
